import { useQuestionnaireWebsiteScanQuery } from '@dieterApi/scan/useQuestionnaireWebsiteScanQuery';
import { useScanPrefillForCategoryQuery } from '@dieterApi/scan/useScanPrefillForCategoryQuery';
import { Button, CircularProgress } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  category: string;
  value: string[];
  onChange: (value: string[]) => void;
}

/**
 * Banner shown above a service-selection question listing the services the
 * embedded website scan detected for this category. Detected services that
 * the user has not yet selected are offered for one-click adoption (either
 * all at once or individually). Nothing is added automatically — adoption is
 * always an explicit user action, so a detected-but-unwanted service is
 * simply left un-added.
 *
 * While the scan is still running and nothing has been detected for this
 * category yet, a lightweight "scan in progress" hint is shown instead,
 * nudging the user to wait a moment for the pre-fill to arrive.
 */
export function ScanPrefillBanner({ category, value, onChange }: Props) {
  const { t } = useTranslation();
  const { data, refetch } = useScanPrefillForCategoryQuery(category);
  const { data: scanData } = useQuestionnaireWebsiteScanQuery();

  const detected = data?.scanPrefillForCategory ?? [];

  // Only surface detected services the user has not already selected.
  const pending = useMemo(() => detected.filter((name) => !value.includes(name)), [detected, value]);

  const scan = scanData?.questionnaireWebsiteScan;
  const scanExists = !!scan && scan.status !== 'none' && !!scan.propertyId;
  const scanRunning = scanExists && !scan!.scanFinished && scan!.status !== 'failed';
  const scanFinished = scanExists && !!scan!.scanFinished;

  // The prefill query runs once on mount and would otherwise keep returning the
  // empty result it cached while the scan was still running. Once the scan
  // reaches its terminal state the backend has written the detected services,
  // so refetch to pull them in and update the banner without a page reload.
  useEffect(() => {
    if (scanFinished) refetch();
  }, [scanFinished, refetch]);

  // Nothing detected yet, but the scan is still working: hint the user to wait.
  if (pending.length === 0 && scanRunning) {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
        <div className="flex items-center gap-3">
          <CircularProgress size={18} />
          <p className="text-sm text-gray-700">{t('components.block.quest.scan.prefill.running_hint')}</p>
        </div>
      </div>
    );
  }

  // Scan finished but found nothing for this category: remind the user that the
  // scan can miss things and they should still review the list themselves. Only
  // when nothing was detected at all — if services were detected but the user
  // already added them, the banner simply vanishes (handled below).
  if (pending.length === 0 && scanFinished && detected.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-start gap-2">
          <span className="text-lg leading-none">ℹ️</span>
          <p className="flex-1 text-sm text-gray-700">{t('components.block.quest.scan.prefill.none_found')}</p>
        </div>
      </div>
    );
  }

  if (pending.length === 0) return null;

  const addOne = (name: string) => {
    if (!value.includes(name)) onChange([...value, name]);
  };

  const addAll = () => {
    const merged = Array.from(new Set([...value, ...pending]));
    onChange(merged);
  };

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
      <div className="flex items-start gap-2">
        <span className="text-lg leading-none">🔍</span>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-800">
            {t('components.block.quest.scan.prefill.detected', { count: pending.length })}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {pending.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => addOne(name)}
                className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-white px-3 py-1 text-sm text-gray-700 transition hover:bg-primary/10"
                title={t('components.block.quest.scan.prefill.add')}
              >
                <span>+</span>
                {name}
              </button>
            ))}
          </div>
          <div className="mt-3">
            <Button variant="contained" size="small" onClick={addAll} disableElevation>
              {t('components.block.quest.scan.prefill.adopt', { count: pending.length })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
